import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Eye, Users, Music, Volume2, Maximize2, MapPin, BadgeCheck, Navigation, Loader2 } from "lucide-react";
import { locationLabel } from "@/lib/mappers";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const fmtK = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}K` : `${n}`);

export function FeaturedHero({ featured, onNearMe, nearMeLoading = false, nearMeActive = false }) {
  return (
    <section
      data-testid="featured-hero"
      className="overflow-hidden rounded-3xl bg-secondary/60 p-6 sm:p-8 lg:p-10"
    >
      <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-12">
        {/* Left — content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <span className="inline-flex items-center gap-2 rounded-full bg-brand/10 px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide text-brand">
            <span className="h-2 w-2 rounded-full bg-brand" />
            {featured.tagline}
          </span>

          <h1 className="mt-5 font-heading text-4xl font-extrabold leading-none tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            {featured.title}
          </h1>
          <p className="mt-3 font-heading text-xl font-semibold text-muted-foreground sm:text-2xl">
            {featured.subtitle}
          </p>
          <p className="mt-4 max-w-md text-base text-muted-foreground">
            {featured.description}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm font-medium">
            <span className="flex items-center gap-2 text-live">
              <span className="h-2.5 w-2.5 rounded-full bg-live" /> LIVE
            </span>
            <span className="flex items-center gap-2 text-muted-foreground">
              <Users className="h-4 w-4" /> {fmtK(featured.viewers)} watching
            </span>
            <span className="flex items-center gap-2 text-muted-foreground">
              <Music className="h-4 w-4" /> {featured.category}
            </span>
          </div>

          {/* scrubber */}
          <div className="mt-6 h-1.5 w-56 max-w-full rounded-full bg-border">
            <div className="relative h-full w-1/4 rounded-full bg-brand">
              <span className="absolute -right-1.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 rounded-full bg-brand ring-4 ring-white" />
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link
              to={`/profile/${featured.host.username}`}
              className="flex items-center gap-2.5"
              data-testid="hero-host"
            >
              <Avatar className="h-11 w-11">
                <AvatarImage src={featured.host.avatar} alt={featured.host.displayName} />
                <AvatarFallback>{featured.host.displayName[0]}</AvatarFallback>
              </Avatar>
              <span className="flex items-center gap-1 font-semibold text-foreground">
                {featured.host.displayName}
                {featured.host.verified && <BadgeCheck className="h-4 w-4 text-brand" />}
              </span>
            </Link>

            <Button
              asChild
              data-testid="hero-watch-btn"
              className="rounded-full bg-brand px-7 py-6 text-base hover:bg-brand-hover"
            >
              <Link to={`/live/${featured.id}`}>
                <Play className="mr-1 h-5 w-5 fill-white" /> Watch live now
              </Link>
            </Button>
            <Button
              asChild
              data-testid="hero-explore-btn"
              variant="outline"
              className="rounded-full border-border bg-white px-7 py-6 text-base hover:bg-secondary"
            >
              <Link to="/explore">Explore all live</Link>
            </Button>

            {onNearMe && (
              <Button
                type="button"
                data-testid="hero-near-me-btn"
                variant="outline"
                onClick={onNearMe}
                disabled={nearMeLoading}
                className={`rounded-full border-border px-7 py-6 text-base hover:bg-secondary ${
                  nearMeActive ? "bg-brand/10 text-brand" : "bg-white"
                }`}
              >
                {nearMeLoading ? (
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                ) : (
                  <Navigation className="mr-2 h-5 w-5" />
                )}
                {nearMeActive ? "Nearest live shown" : "Show live near me"}
              </Button>
            )}
          </div>
        </motion.div>

        {/* Right — video card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
        >
          <Link
            to={`/live/${featured.id}`}
            data-testid="hero-video-card"
            className="group relative block aspect-[16/10] overflow-hidden rounded-2xl bg-[#0A1526] shadow-xl ring-1 ring-black/5"
          >
            <img
              src={featured.hero}
              alt={featured.title}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/10" />

            <div className="absolute left-4 top-4 flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-live px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
                <span className="h-1.5 w-1.5 rounded-full bg-white" /> Live
              </span>
              <span className="flex items-center gap-1.5 rounded-full bg-black/55 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-md">
                <Eye className="h-3.5 w-3.5" /> {fmtK(featured.viewers)}
              </span>
            </div>

            <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-brand/90 text-white shadow-2xl">
                <Play className="ml-1 h-7 w-7 fill-white" />
              </span>
            </div>

            <div className="absolute bottom-4 left-4 flex items-center gap-1.5 text-sm font-medium text-white">
              <MapPin className="h-4 w-4" /> {locationLabel(featured.location)}
            </div>
            <div className="absolute bottom-4 right-4 flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-md">
                <Volume2 className="h-4 w-4" />
              </span>
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-md">
                <Maximize2 className="h-4 w-4" />
              </span>
            </div>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

export default FeaturedHero;
